import { useCallback, useEffect, useState } from 'react';
import { Link, useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { apiClient } from '@/lib/apiClient';
import { fmt } from '@/lib/format';
import { formatJalaliDate } from '@/lib/format';
import { ordersApi } from '@/lib/apiServices';
import { PAYMENT_MESSAGES } from '../paymentCopy';
import {
  navigateToPaymentRedirect,
  paymentErrorMessage,
  paymentOptionUnavailableMessage,
} from '../payment';
import { PAYMENT_STATUS_LABELS } from '../lib/orderStatus';
import '../orders.css';

export default function PaymentResultPage() {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const transactionId = searchParams.get('transaction_id');
  const orderId = searchParams.get('order_id');
  const receiptUsed = searchParams.get('error') === 'receipt_used';
  const [transaction, setTransaction] = useState(null);
  const [paidOrder, setPaidOrder] = useState(null);
  const [loadedOrderId, setLoadedOrderId] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(Boolean(transactionId || orderId) && !receiptUsed);
  const [retrying, setRetrying] = useState(false);
  const [timedOut, setTimedOut] = useState(false);
  const [pendingModeMismatch, setPendingModeMismatch] = useState(false);
  const [savedChequePlan, setSavedChequePlan] = useState(null);

  const loadTransaction = useCallback(async () => {
    if (!transactionId || receiptUsed) return null;
    const { data, error: apiError, response } = await apiClient.GET(
      '/api/v1/finance/transactions/{transaction_id}',
      { params: { path: { transaction_id: transactionId } } },
    );
    if (apiError || !data) {
      if (response?.status === 401 || response?.status === 403) {
        navigate('/login', {
          replace: true,
          state: { from: { pathname: location.pathname, search: location.search } },
        });
        return null;
      }
      setError('دریافت نتیجه پرداخت ممکن نشد. لطفاً از بخش سفارش‌ها پیگیری کنید.');
      return null;
    }
    setTransaction(data);
    if (data.payment_mode === 'cheque' && data.order_id) {
      try {
        const savedPlan = window.sessionStorage.getItem(
          `payment-cheque-plan:${data.order_id}`,
        );
        setSavedChequePlan(savedPlan ? JSON.parse(savedPlan) : null);
      } catch (storageError) {
        console.error('Unable to read retained cheque payment terms:', storageError);
        setSavedChequePlan(null);
      }
    } else {
      setSavedChequePlan(null);
    }
    setError('');
    return data;
  }, [location.pathname, location.search, navigate, receiptUsed, transactionId]);

  useEffect(() => {
    if (!transactionId || receiptUsed) return undefined;
    let active = true;
    const startedAt = Date.now();
    let timer;
    const poll = async () => {
      if (!active) return;
      setLoading(true);
      const latest = await loadTransaction();
      if (!active) return;
      setLoading(false);
      if (latest?.status !== 'pending') return;
      if (Date.now() - startedAt >= 120000) {
        setTimedOut(true);
        return;
      }
      timer = window.setTimeout(poll, 4000);
    };
    poll();
    return () => {
      active = false;
      window.clearTimeout(timer);
    };
  }, [loadTransaction, receiptUsed, transactionId]);

  useEffect(() => {
    if (transactionId || !orderId || receiptUsed) return undefined;
    let active = true;
    ordersApi.get(orderId)
      .then(({ data }) => {
        if (!active) return;
        setLoadedOrderId(orderId);
        if (!data.is_paid) {
          setError('این سفارش هنوز پرداخت‌شده نیست. برای پیگیری به سفارش‌های خود مراجعه کنید.');
          return;
        }
        setPaidOrder(data);
        setError('');
      })
      .catch(() => {
        if (active) {
          setLoadedOrderId(orderId);
          setError('دریافت نتیجه پرداخت ممکن نشد. لطفاً از بخش سفارش‌ها پیگیری کنید.');
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [orderId, receiptUsed, transactionId]);

  const handleRetry = async () => {
    if (!transaction?.order_id || !canRetryWithTerms) return;
    if (transaction.payment_mode === 'cheque' && !savedChequePlan) {
      setError(PAYMENT_MESSAGES.chequeRetryUnavailable);
      return;
    }
    setRetrying(true);
    setError('');
    setPendingModeMismatch(false);
    try {
      const { data, error: apiError, response } = await apiClient.POST(
        '/api/v1/finance/initiate',
        {
          body: {
            order_id: transaction.order_id,
            method: 'debit',
            payment_mode: transaction.payment_mode,
            ...(transaction.payment_mode === 'cheque' && {
              cheque_prepayment_amount: savedChequePlan.prepayment,
              cheque_amounts: savedChequePlan.amounts,
            }),
          },
        },
      );
      if (apiError || !data) {
        const code = apiError?.code ?? apiError?.detail?.code;
        if (response?.status === 409 && code === 'PAYMENT_OPTION_UNAVAILABLE') {
          const { data: paymentOptions } = await ordersApi.paymentOptions(transaction.order_id);
          const reason = paymentOptions?.find(
            (option) => option.option === transaction.payment_mode,
          )?.reason;
          setError(paymentOptionUnavailableMessage(
            reason ?? apiError?.detail?.reason,
          ));
          return;
        }
        setPendingModeMismatch(code === 'PENDING_PAYMENT_MODE_MISMATCH');
        setError(paymentErrorMessage(apiError, response?.status));
      } else if (!data.payment_required) {
        navigate(`/profile/orders/${transaction.order_id}`, { replace: true });
      } else {
        navigateToPaymentRedirect(data.redirect);
      }
    } catch {
      setError(PAYMENT_MESSAGES.genericError);
    } finally {
      setRetrying(false);
    }
  };

  if (receiptUsed) {
    return (
      <main className="orders-detail-page">
        <section className="orders-state orders-state--error" role="alert">
          {PAYMENT_MESSAGES.receiptUsed}
        </section>
        <Link to="/profile/orders">مشاهده سفارش‌ها</Link>
      </main>
    );
  }

  if (!transactionId && !orderId) {
    return <main className="orders-detail-page">{PAYMENT_MESSAGES.failure}</main>;
  }

  if (
    (loading || (orderId && loadedOrderId !== orderId))
    && !transaction
    && !paidOrder
  ) {
    return <main className="orders-state orders-detail-page" role="status">{PAYMENT_MESSAGES.pending}</main>;
  }

  if (error && !transaction && !paidOrder) {
    return (
      <main className="orders-detail-page">
        <div className="orders-state orders-state--error" role="alert">{error}</div>
        <Link to="/profile/orders">مشاهده سفارش‌ها</Link>
      </main>
    );
  }

  if (paidOrder) {
    return (
      <main className="orders-detail-page" aria-live="polite">
        <section className="card" style={{ padding: '2rem', textAlign: 'center' }}>
          <h1>پرداخت با موفقیت انجام شد</h1>
          <p>سفارش شما با موفقیت پرداخت شده است.</p>
          <Link className="btn btn-primary" to={`/profile/orders/${paidOrder.id}`}>مشاهده سفارش</Link>
        </section>
        {paidOrder.total_price_amount != null && (
          <p className="order-card-meta">مبلغ سفارش: {fmt(paidOrder.total_price_amount)} تومان</p>
        )}
      </main>
    );
  }
  if (!transaction) return null;
  const status = transaction.status;
  const canRetryPaymentMode = [
    'normal',
    'secure',
    'installment',
  ].includes(transaction.payment_mode);
  const canRetryWithTerms = canRetryPaymentMode
    || (
      transaction.payment_mode === 'cheque'
      && Boolean(savedChequePlan?.prepayment)
      && Array.isArray(savedChequePlan.amounts)
      && savedChequePlan.amounts.length > 0
      && savedChequePlan.amounts.every(Boolean)
    );

  return (
    <main className="orders-detail-page" aria-live="polite">
      {status === 'verified' || status === 'already_verified' ? (
        <section className="card" style={{ padding: '2rem', textAlign: 'center' }}>
          <h1>پرداخت با موفقیت انجام شد</h1>
          <p>شماره رسید: <strong>{transaction.ref_id || '—'}</strong></p>
          {transaction.verified_at && <p>زمان پرداخت: {formatJalaliDate(transaction.verified_at, { includeTime: true })}</p>}
          {transaction.order_id && <Link className="btn btn-primary" to={`/profile/orders/${transaction.order_id}`}>مشاهده سفارش</Link>}
        </section>
      ) : status === 'pending' ? (
        <section className="card" aria-live="polite" role="status" style={{ padding: '2rem', textAlign: 'center' }}>
          <h1>{timedOut ? PAYMENT_MESSAGES.pendingTimeout : PAYMENT_MESSAGES.pending}</h1>
          {transaction.order_id && <Link to={`/profile/orders/${transaction.order_id}`}>رفتن به سفارش‌ها</Link>}
        </section>
      ) : (
        <section className="card" style={{ padding: '2rem', textAlign: 'center' }}>
          <h1>{status === 'expired' ? PAYMENT_MESSAGES.expired : PAYMENT_MESSAGES.failure}</h1>
          <p>{PAYMENT_STATUS_LABELS[status] ?? 'ناموفق'}</p>
          <p>{PAYMENT_MESSAGES.refundNote}</p>
          {error && <div className="orders-state orders-state--error" role="alert">{error}</div>}
          {canRetryWithTerms ? (
            <button className="btn btn-primary" disabled={retrying} onClick={handleRetry} type="button">
              {retrying ? PAYMENT_MESSAGES.preparingPayment : PAYMENT_MESSAGES.retryPayment}
            </button>
          ) : (
            <p className="order-payment-status" role="status">
              {transaction.payment_mode === 'cheque'
                ? PAYMENT_MESSAGES.chequeRetryUnavailable
                : PAYMENT_MESSAGES.paymentModeUnavailable}
            </p>
          )}
          {transaction.order_id && <p><Link to={`/profile/orders/${transaction.order_id}`}>مشاهده سفارش</Link></p>}
          {pendingModeMismatch && (
            <div className="payment-pending-actions">
              <Link className="payment-pending-order-link" to={`/profile/orders/${transaction.order_id}`}>
                {PAYMENT_MESSAGES.viewPendingOrder}
              </Link>
              <Link className="payment-pending-order-link" to="/profile/orders">
                {PAYMENT_MESSAGES.waitForPendingPayment}
              </Link>
            </div>
          )}
        </section>
      )}
      {transaction.amount != null && <p className="order-card-meta">مبلغ پرداخت: {fmt(transaction.amount)} تومان</p>}
    </main>
  );
}
